import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import Input from './Input';
import Button from './Button';

const ContactSearchBar = ({ 
  onSearchQuery = () => {},
  searchQuery = '',
  onContactSelect = () => {},
  contacts = [],
  placeholder = "Search contacts...",
  showFilters = false,
  onFilterChange = () => {},
  filters = {},
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const searchRef = useRef(null);
  const resultsRef = useRef(null);

  useEffect(() => {
    if (searchQuery?.trim()) {
      const filtered = contacts?.filter(contact => 
        contact?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        contact?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        contact?.phone?.includes(searchQuery) ||
        contact?.company?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      )?.slice(0, 8); // Limit to 8 results
      
      setFilteredContacts(filtered);
      setShowResults(filtered?.length > 0);
    } else {
      setFilteredContacts([]);
      setShowResults(false);
    }
  }, [searchQuery, contacts]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef?.current && 
        !searchRef?.current?.contains(event?.target) &&
        resultsRef?.current &&
        !resultsRef?.current?.contains(event?.target)
      ) {
        setShowResults(false);
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (e) => {
    onSearchQuery(e?.target?.value);
  };

  const handleContactClick = (contact) => {
    onContactSelect(contact);
    setShowResults(false);
    setIsExpanded(false);
  };

  const handleClearSearch = () => {
    onSearchQuery('');
    setShowResults(false);
  };

  const handleFilterToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={`relative ${className}`}>
      {/* Main Search Input */}
      <div ref={searchRef} className="relative">
        <div className="relative">
          <Input
            type="search"
            placeholder={placeholder}
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => searchQuery && setShowResults(true)}
            className="pl-10 pr-20"
          />
          <Icon 
            name="Search" 
            size={18} 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" 
          />
          
          {/* Action Buttons */}
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClearSearch}
                className="h-6 w-6"
              >
                <Icon name="X" size={14} />
              </Button>
            )}
            
            {showFilters && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleFilterToggle}
                className={`h-6 w-6 ${isExpanded ? 'text-primary' : ''}`}
              >
                <Icon name="Filter" size={14} />
              </Button>
            )}
          </div>
        </div>

        {/* Search Results Dropdown */}
        {showResults && (
          <div 
            ref={resultsRef}
            className="absolute top-full left-0 right-0 mt-1 bg-surface border border-border rounded-lg shadow-modal z-300 max-h-80 overflow-y-auto"
          >
            {filteredContacts?.map((contact, index) => (
              <button
                key={contact?.id || index}
                onClick={() => handleContactClick(contact)}
                className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-muted transition-micro text-left border-b border-border last:border-b-0"
              >
                <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                  {contact?.avatar ? (
                    <img 
                      src={contact?.avatar} 
                      alt={contact?.name}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <Icon name="User" size={20} className="text-text-secondary" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-text-primary truncate">
                    {contact?.name}
                  </div>
                  <div className="text-sm text-text-secondary truncate">
                    {contact?.email || contact?.phone || contact?.company}
                  </div>
                </div>
                <Icon name="ChevronRight" size={16} className="text-text-secondary flex-shrink-0" />
              </button>
            ))}
            
            {searchQuery && filteredContacts?.length === 0 && (
              <div className="px-4 py-6 text-center text-text-secondary">
                <Icon name="Search" size={24} className="mx-auto mb-2 opacity-50" />
                <p>No contacts found for "{searchQuery}"</p>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Advanced Filters - Expandable */}
      {showFilters && isExpanded && (
        <div className="mt-3 p-4 bg-muted rounded-lg border border-border animate-slide-up">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Category
              </label>
              <select 
                className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text-primary"
                value={filters?.category || ''}
                onChange={(e) => onFilterChange({ ...filters, category: e?.target?.value })}
              >
                <option value="">All Categories</option>
                <option value="work">Work</option>
                <option value="personal">Personal</option>
                <option value="family">Family</option>
                <option value="client">Client</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Location
              </label>
              <Input
                type="text"
                placeholder="City or Country"
                value={filters?.location || ''}
                onChange={(e) => onFilterChange({ ...filters, location: e?.target?.value })}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Company
              </label>
              <Input
                type="text"
                placeholder="Company name"
                value={filters?.company || ''}
                onChange={(e) => onFilterChange({ ...filters, company: e?.target?.value })}
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onFilterChange({})}
            >
              Clear Filters
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => setIsExpanded(false)}
            >
              Apply Filters
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactSearchBar;